import React, { useState, MouseEvent, ReactNode, useEffect, ReactElement } from "react";
import { MenuItem, Popper, Paper, ClickAwayListener, MenuList, Grow, CircularProgress } from "@mui/material";
import Buttons, {Btn} from "../Buttons/Buttons";
import { ReactComponent as OpenIcon } from '../../assets/Icons/chevron/Chevron-Down.svg';
import { ReactComponent as CloseIcon } from '../../assets/Icons/chevron/Chevron-Up.svg';
import { ReactComponent as OpenNestedMenuIcon } from '../../assets/Icons/chevron/Chevron-Right.svg';
import { ReactComponent as DotsIcon } from '../../assets/Icons/adornments/Dots.svg';
import { DropdownOption } from "./Dropdown";


export default function NestedDropdown({ btn, options, dir }: { 
    btn?: Btn,
    options: DropdownOption[],
    dir: 'right' | 'left',
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [nestedAnchorEl, setNestedAnchorEl] = useState<null | HTMLElement>(null);
   const [ nestedOptions, setNestedOptions ] = useState<{
        label: string,
        action: (e:MouseEvent<HTMLButtonElement>) => void | Promise<void>,
        startingAdornment?: ReactNode,
    }[]>([]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ loadingNestedIndex, setLoadingNestedIndex ] = useState(0);

    useEffect(() => {
        if (options.length > 0) {
            const withMoreOptions = options.find(option => option.moreOptions !== undefined);
            if (withMoreOptions !== undefined && withMoreOptions.moreOptions !== undefined) {
                setNestedOptions(withMoreOptions.moreOptions);
            }
        }
    }, [options]);


     // Open/close main menu
  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget); // Toggle menu
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setNestedAnchorEl(null);
  };

  // Open/close nested menu
  const handleOpenNestedMenu = (event: MouseEvent<HTMLElement>) => {
    setNestedAnchorEl(nestedAnchorEl ? null : event.currentTarget); // Toggle nested menu
  };

  const performNestedAction = async (
    e: MouseEvent<HTMLButtonElement>, 
    option: {
        label: string,
        action: (e:MouseEvent<HTMLButtonElement>) => void | Promise<void>,
        startingAdornment?: ReactNode,
    },
    index: number,
  ) => {
    e.preventDefault();

    setIsLoading(true);
    setLoadingNestedIndex(index);

    await option.action(e);

    setIsLoading(false);
    handleCloseMenu();
  }

  return (
        <>
        {btn ? (
            <Buttons 
                  type={btn.type}
                  styling={btn.styling}
                  id={btn.id}
                  label={btn.label}
                  endingAdornment={Boolean(anchorEl) ? <CloseIcon/> : <OpenIcon/>}
                  action={handleOpenMenu}
          />
        ): (
            <button onClick={handleOpenMenu} className={`${Boolean(anchorEl) ? 'text-primary' : 'text-disable'} hover:text-primary transition-all`}>
                <div className='w-[24px] h-[24px]'><DotsIcon/></div>
            </button>
        )}
    
          {/* Main Menu */}
          <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 50 }} transition>
            {({ TransitionProps }) => (
            <Grow {...TransitionProps} in={Boolean(anchorEl)} style={{ transformOrigin: '0 0 0' }}>
            <Paper
                sx={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginTop: '8px',
                }}
            >
              <ClickAwayListener onClickAway={(e:any) => {!isLoading && handleCloseMenu(); e.stopPropagation()}}>
                <MenuList
                    sx={{
                        padding: 0,
                    }}
                >
                {options.map((option, i) => (
                    <MenuItem onKeyDown={(e) => e.stopPropagation()} key={i} sx={{
                        fontFamily: 'Montserrat',
                        fontWeight: 'medium',
                        color: `#35424A`,
                        backgroundColor: `#ffffff`,
                        padding: 0,
                    }} onClick={(e:any) => {option.action && option.action(e); option.moreOptions && handleOpenNestedMenu(e); e.stopPropagation()}}>
                      {option.btnComp ? (
                        <>
                        { option.btnComp = React.Children.map(option.btnComp, el => {
                            return React.cloneElement(el as ReactElement, { handleCloseMenu })
                        })}
                        </>
                        // <>{option.btnComp}</>
                      ) : (
                        <button onClick={(e:any) => {!option.moreOptions && handleCloseMenu()}} className={`w-full px-5 py-3 text-left font-medium flex justify-between items-center gap-3 w-full h-full`}>
                            <div className='flex justify-start items-center gap-3'>
                                {/* {option.startingAdornment && <div className='w-[18px] h-[18px]'>{option.startingAdornment}</div>} */}
                                <p className='text-nowrap'>{option.label}</p>
                            </div>
                            {option.moreOptions && <div className='w-[14px] h-[14px]'><OpenNestedMenuIcon/></div>}
                        </button>
                      )}
                    </MenuItem>
    
                ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
            </Grow>
            )}
          </Popper>
    
          {/* Nested Menu */}
          <Popper open={Boolean(nestedAnchorEl)} anchorEl={nestedAnchorEl} placement={`${dir === 'right' ? 'right-start' : 'left-start'}`} sx={{ zIndex: 50 }} transition>
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} in={Boolean(anchorEl)} style={{ transformOrigin: '0 0 0' }}>
            <Paper
                sx={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginLeft: dir === 'right' ? '8px' : '0',
                    marginRight: dir === 'left' ? '8px' : '0',
                }}
            >
              <ClickAwayListener onClickAway={() => setNestedAnchorEl(null)}>
                <MenuList
                    sx={{
                        padding: 0,
                    }}
                >
                  {nestedOptions.length > 0 && nestedOptions.map((option, i) => (
                    <MenuItem key={i} sx={{
                        fontFamily: 'Montserrat',
                        fontWeight: 'medium',
                        color: `#35424A`,
                        backgroundColor: `#ffffff`,
                        padding: '12px 20px',
                    }} onClick={(e:any) => performNestedAction(e, option, i)}>
                        <button className={`text-left font-medium flex justify-between items-center gap-3 w-full h-full`}>
                            <div className='flex justify-start items-center gap-3'>
                                {option.startingAdornment && <div className='w-[18px] h-[18px]'>{option.startingAdornment}</div>}
                                <p className='text-nowrap'>{option.label}</p>
                            </div>
                            {isLoading && loadingNestedIndex === i && (
                              <CircularProgress size={14} color='inherit'/>
                            )}
                        </button>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
            </Grow>
            )}
          </Popper>
        </>
      );
};



